export const requiredValidateEmail = (value: string): string | undefined => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!value) {
    return "Email is required.";
  }
  if (!emailRegex.test(value)) {
    return "Invalid email address.";
  }
  return undefined;
};

export const requiredValidatePhoneNumber = (value: string): string | undefined => {
  const phoneNumberPattern = /^\d{10}$/;
  if (!value) {
    return "Phone number is required.";
  }
  if (!phoneNumberPattern.test(value)) {
    return "Phone number must be a 10-digit number.";
  }
  return undefined;
};

// Username is a login name, NOT an email: letters, numbers, underscore, dot.
export const validateUsername = (value: string): string | undefined => {
  if (!value) {
    return "Username is required";
  }
  if (value.length < 3) {
    return "Username must be at least 3 characters";
  }
  if (value.length > 50) {
    return "Username must be at most 50 characters";
  }
  if (!/^[A-Za-z0-9_.]+$/.test(value)) {
    return "Username can only contain letters, numbers, underscore and dot";
  }
  return undefined;
};

// Required contact/reset email for a user account.
export const validateUserEmail = (value: string): string | undefined => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!value) {
    return "Email address is required";
  }
  if (!emailRegex.test(value)) {
    return "Email address is invalid";
  }
  return undefined;
};

// Email rule (canonical):
//   • Non-empty local part with only common address characters.
//   • Exactly one @.
//   • Domain of 1+ labels separated by ".", each label 1-63 chars, letters /
//     digits / hyphens, not starting or ending with a hyphen.
//   • Final TLD is 2-24 LETTERS only. This is the stricter rule that
//     rejects things like `vmadmin@gmail.cw211om` (digits in the TLD),
//     `vmadmin@gmail` (no TLD), and `vmadmin@gmail.` (empty TLD) — the
//     old rule accepted all three.
//   • No spaces, no consecutive dots, capped at 254 characters.
const EMAIL_RE = /^(?=.{1,254}$)[A-Za-z0-9._%+\-]+(?<!\.)@(?:(?!-)[A-Za-z0-9-]{1,63}(?<!-)\.)+[A-Za-z]{2,24}$/;

export const validateEmail = (value: string): string | undefined => {
  if (value === "" || value == null) return undefined;
  const cleaned = String(value).trim();
  if (!cleaned) return undefined;
  if (cleaned.length > 254) return "Enter a valid email address.";
  if (cleaned.includes("..")) return "Enter a valid email address.";
  if (!EMAIL_RE.test(cleaned)) return "Enter a valid email address.";
  return undefined;
};

// export const validatePhoneNumber = (value: string): string | undefined => {
//   const phoneNumberPattern = /^\d{10}$/;
//   if (value && !phoneNumberPattern.test(value)) {
//     return "Phone number must be a 10-digit number.";
//   }
//   return undefined;
// };

export const validatePhoneNumber = (value: string): string | undefined => {
  // Normalise the same way the input formatter does (strip everything that
  // isn't a digit) so the validator agrees with the sanitiser and can't
  // false-fail on a value the user is halfway through typing.
  const digits = (value || "").replace(/\D/g, "");
  if (!digits) return undefined;
  if (!/^\d{10}$/.test(digits)) {
    return "Enter a valid 10-digit phone number.";
  }
  return undefined;
};

// export const formatPhoneNumberForDisplay = (value: string): string => {
//   const strippedValue = value.replace(/\D/g, ""); // Remove all non-digit characters
//   const match = strippedValue.match(/^(\d{3})(\d{3})(\d{4})$/);
//   return match ? `${match[1]}-${match[2]}-${match[3]}` : value;
// };

export const formatPhoneNumberForDisplay = (value: string | null | undefined): string => {
  if (!value) {
    return ''; // Return an empty string or some default value if the input is null or undefined
  }

  const strippedValue = value.replace(/\D/g, ""); // Remove all non-digit characters
  const match = strippedValue.match(/^(\d{3})(\d{3})(\d{4})$/);
  return match ? `${match[1]}-${match[2]}-${match[3]}` : value;
};

// ── Canadian/US phone helpers (shared by every parent contact input) ──
// normalizePhoneNumber → the value SENT to and STORED by the backend: digits
// only, capped at 10. formatPhoneNumber → the value SHOWN to the user, formatted
// progressively as they type (647 → 647, 6475 → 647-5, 6475675666 → 647-567-5666).
// Splitting display from storage keeps the input always formatted while the API
// always receives raw digits — and tolerates existing values stored in either
// format, since both collapse to the same 10 digits first.
export const normalizePhoneNumber = (value: string | null | undefined): string =>
  (value || "").replace(/\D/g, "").slice(0, 10);

export const formatPhoneNumber = (value: string | null | undefined): string => {
  const digits = normalizePhoneNumber(value);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
};

// True when the value contains exactly 10 digits (dashes/formatting ignored).
export const isValidPhoneNumber = (value: string | null | undefined): boolean =>
  normalizePhoneNumber(value).length === 10;

// ── Money (currency) helpers ──
// Kept as a STRING in form state so the user can freely clear/edit while typing.
// sanitizeMoneyInput allows digits + a single dot + at most 2 decimals, and no
// sign (so negatives are impossible). formatMoney is applied on blur only, so
// deleting never snaps the field back to 0.00 mid-edit.
export const sanitizeMoneyInput = (value: string): string => {
  let v = (value || "").replace(/[^\d.]/g, ""); // digits + dots only → no letters, no minus
  const firstDot = v.indexOf(".");
  if (firstDot !== -1) {
    const intPart = v.slice(0, firstDot);
    const dec = v.slice(firstDot + 1).replace(/\./g, "").slice(0, 2); // one dot, max 2 decimals
    v = intPart + "." + dec;
  }
  return v;
};

// "40" → "40.00", "40.5" → "40.50", "" → "" (empty stays empty for blur/load).
export const formatMoney = (value: string | number | null | undefined): string => {
  if (value === "" || value === null || value === undefined) return "";
  const n = Number(value);
  return isNaN(n) ? "" : n.toFixed(2);
};

export const isValidMoney = (value: string | number | null | undefined): boolean => {
  if (value === "" || value === null || value === undefined) return false;
  const n = Number(value);
  return !isNaN(n) && n >= 0;
};

// ── Billing day (1–28) ──
// Digits only, max 2 chars; empty allowed WHILE editing (validation handles the
// required/range rules). Never coerces back to a default during typing.
export const sanitizeBillingDay = (value: string): string =>
  (value || "").replace(/\D/g, "").slice(0, 2);

export const isValidBillingDay = (value: string | number | null | undefined): boolean => {
  if (value === "" || value === null || value === undefined) return false;
  const n = Number(value);
  return Number.isInteger(n) && n >= 1 && n <= 28;
};

export const formatPhoneNumberForDisplaySpace = (value: string | null | undefined): string => {
  if (!value) {
    return ''; // Return an empty string or some default value if the input is null or undefined
  }

  const strippedValue = value.replace(/\D/g, ""); // Remove all non-digit characters
  const match = strippedValue.match(/^(\d{3})(\d{3})(\d{4})$/);
  return match ? `${match[1]}${" "}${match[2]}${" "}${match[3]}` : value;
};
